import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { webApiClient } from '../lib/fi-api/apiUtils';
import { useLocale } from './LocaleContext';
import { IImage, IVideo, createImage, createVideo } from '../types';
import { getFiGrowthBook } from '../lib/growthbook';
import * as types from '../types';

interface ApiMediaUrl {
  descriptor: string;
  url: string;
  webpUrl?: string;
}

interface ApiMediaItem {
  type?: 'image' | 'video';
  urls: ApiMediaUrl[];
  position: number;
  thumbnailUrl?: string;
}

interface BatchResponse {
  permutations: {
    'mini-dog': ApiMediaItem[] | null;
    'mini-cat': ApiMediaItem[] | null;
    s3plus: ApiMediaItem[] | null;
    ultra: ApiMediaItem[] | null;
  };
}

type PdpMedia = IImage | IVideo;

/**
 * Detect WebP support - cached result
 */
let webpSupported: boolean | null = null;
function supportsWebP(): boolean {
  if (webpSupported !== null) return webpSupported;

  if (typeof document === 'undefined') {
    webpSupported = false;
    return false;
  }

  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  webpSupported = canvas.toDataURL('image/webp').startsWith('data:image/webp');
  return webpSupported;
}

/**
 * Get the best URL for an image based on browser support
 */
function getBestImageUrl(urls: ApiMediaUrl[]): string {
  if (!urls.length) return '';

  const primaryUrl = urls[0];
  if (supportsWebP() && primaryUrl.webpUrl) {
    return primaryUrl.webpUrl;
  }
  return primaryUrl.url;
}

/**
 * Transform API response to IImage/IVideo format
 */
function transformMedia(items: ApiMediaItem[] | null): PdpMedia[] | null {
  if (!items || items.length === 0) return null;

  return items
    .sort((a, b) => a.position - b.position)
    .map((item): PdpMedia => {
      const mediaType = item.type || 'image';

      if (mediaType === 'video') {
        const thumbnailUrl = item.thumbnailUrl || item.urls[0]?.url || '';
        return createVideo({
          url: item.urls[0]?.url || '',
          thumbnail: createImage({
            url: thumbnailUrl,
          }),
        });
      }

      const bestUrl = getBestImageUrl(item.urls);
      return createImage({
        url: bestUrl,
        descriptors: item.urls.map((u) => u.descriptor),
        ...(item.urls.length > 1 && {
          retinaWidth: parseInt(item.urls[item.urls.length - 1]?.descriptor ?? '0', 10) || undefined,
        }),
      });
    });
}

// Cache structure: productId -> species -> media
interface MediaCache {
  [productId: string]: {
    [species: string]: PdpMedia[] | null;
  };
}

interface PdpImagesContextType {
  /** Get media for a product. Returns null if no dynamic images configured. */
  getMedia: (productId: string, facts?: Record<string, string | number | boolean>) => PdpMedia[] | null;
  /** True while initial fetch is in progress */
  isLoading: boolean;
}

const PdpImagesContext = createContext<PdpImagesContextType>({
  getMedia: () => null,
  isLoading: true,
});

export function PdpImagesProvider({ children }: { children: React.ReactNode }) {
  const { country } = useLocale();
  const [isLoading, setIsLoading] = useState(true);
  const [cache, setCache] = useState<MediaCache>({});

  // Fetch all permutations in a single request
  useEffect(() => {
    const fetchAll = async () => {
      try {
        const gbAttributes = getFiGrowthBook().growthBook.getAttributes();

        const params: Record<string, string> = {
          countryCode: country,
        };
        if (Object.keys(gbAttributes).length > 0) {
          params.facts = JSON.stringify(gbAttributes);
        }

        const response = await webApiClient.get<BatchResponse>('/api/ecommerce/images/batch', { params });

        const newCache: MediaCache = {
          [types.miniCollarId]: {
            dog: transformMedia(response.data.permutations['mini-dog']),
            cat: transformMedia(response.data.permutations['mini-cat']),
          },
          [types.series3PlusCollarId]: {
            default: transformMedia(response.data.permutations.s3plus),
          },
          [types.ultraCollarId]: {
            default: transformMedia(response.data.permutations.ultra),
          },
        };

        setCache(newCache);
      } catch (error) {
        console.debug('Failed to fetch PDP images:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAll();
  }, [country]);

  // Get media for a product
  const getMedia = useCallback(
    (productId: string, facts?: Record<string, string | number | boolean>): PdpMedia[] | null => {
      const productCache = cache[productId];
      if (!productCache) return null;

      // For mini, check species-specific cache
      if (productId === types.miniCollarId && facts?.species) {
        const species = String(facts.species).toLowerCase();
        return productCache[species] ?? productCache.default ?? null;
      }

      // For other products, use default
      return productCache.default ?? null;
    },
    [cache],
  );

  return <PdpImagesContext.Provider value={{ getMedia, isLoading }}>{children}</PdpImagesContext.Provider>;
}

export function usePdpImagesContext() {
  return useContext(PdpImagesContext);
}
